<template>
  <div class="WBUDPaintCell" ref="canvas-container" style="width: 100%">
    <div class="row ma-0 pr-4 pl-4 WBUDPaintCell" style="align-items: center; justify-content: space-between">
      <span>{{ getTitle(cell) }}</span>
      <v-btn text @click="clearSkizze(cellKey)">{{ $t('wb-config.skizze.markierungen_loeschen') }}</v-btn>
    </div>
    <canvas :id="canvasId" :ref="canvasId" @click="(e) => onImgClick(e, cellKey)" />
  </div>
</template>

<script>
import config from '@/common/config';
import { getTitle } from './wb-common';
import vehicleMixin from '@/mixins/vehicleMixin';

export default {
  mixins: [vehicleMixin],
  props: {
    cell: { type: Object, required: true }, // the cell configuration object, in this case something like {"cellName": "WBUDPaintCell","height": 530,"key": "skizze","mandatory": false,"title": "wb-config.skizze.skizze.title"}
    namespace: { type: String, required: true },
    mIndex: { type: String, default: '' },
  },
  data() {
    return {
      getTitle,
      cellWidth: 0,
      canvas: undefined,
      ctx: undefined,
      skizzeWidth: 700,
      skizzeHeight: 490,
    };
  },
  mounted() {
    this.canvas = document.getElementById(this.canvasId);
    this.ctx = this.canvas.getContext('2d');

    window.addEventListener('resize', this.handleResize);
    this.handleResize();

    const containerRef = this.$refs[`canvas-container`];

    if (this.skizzeUrl) {
      containerRef.classList.add('show');
    } else {
      containerRef.classList.add('hide');
    }
  },
  created() {
    // Use nextTick to set canvas size according to wrapping div size
    // because only in nextTick the container is available
    this.$nextTick(() => {
      let canvasContainer = this.$refs[`canvas-container`];
      if (canvasContainer) {
        this.showSkizze();
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    input: {
      get() {
        return this.$store.state[this.namespace].formInput;
      },
      set(val) {
        this.$store.state[this.namespace].formInput = val;
      },
    },
    cellKey() {
      return this.cell.key + this.mIndex;
    },
    selectedFz() {
      return this.cell.skizzenImageKey ? this.input[this.cell.skizzenImageKey] : '';
    },
    skizzenByFzName() {
      //Use same property for wagenbuch and unfalldesk
      return this.$store.state[this.namespace].skizzenByFzName || [];
    },

    skizzeUrl() {
      // console.debug(`compute skizzeUrl from module=${this.namespace} vehicle=${JSON.stringify(this.vehicle,null,2)}, this.skizzenByFzName=${JSON.stringify(this.skizzenByFzName,null,2)}`)
      let currentFz;
      if (!this.vehicle && this.cell.cellName !== 'UDFremdFahrzeugPaintCell') return;
      if (this.namespace === 'wagenbuch') {
        currentFz = this.skizzenByFzName.find((r) => r.fzName === this.vehicle.fzName);
      } else if (this.namespace === 'unfalldesk') {
        if (this.cell.cellName === 'UDFremdFahrzeugPaintCell') {
          //Static image is used here but loaded from upload/wagenbuch
          currentFz = { filename: 'fremdfahrzeug.jpg', checksum: '' };
        } else {
          currentFz = this.skizzenByFzName.find((r) => r.fzName === this.vehicle.fzName);
        }
      } else {
        console.error('Unknown namespace: ' + this.namespace);
      }
      if (this.namespace === 'unfalldesk') {
        return currentFz
          ? `${config.apiUrl}/medias/unfalldesk/wagenbuch-fzg/${currentFz.filename}/${currentFz.checksum ? currentFz.checksum : 'pseudo-checksum'}`
          : null; // use a pseudo checksum if no is defined. Otherwise the endpoint does not work
      }
      return currentFz ? `${config.apiUrl}/medias/wagenbuch/${currentFz.filename}/${currentFz.checksum}` : null;
    },
    canvasId() {
      return 'skizze-' + this.cell.key + '-' + this.mIndex;
    },
  },
  methods: {
    setSkizzeDimensions() {
      if (this.skizzeUrl) {
        let skizze = new Image();
        skizze.onload = () => {
          this.skizzeWidth = skizze.width;
          this.skizzeHeight = skizze.height;
        };
        skizze.src = this.skizzeUrl;
      }
    },
    handleResize() {
      this.setCellWidth();
    },
    setCellWidth() {
      this.cellWidth = this.$refs['canvas-container'].clientWidth;
    },
    clearSkizze(key) {
      this.input[key] = [];
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.showSkizze();
    },
    showSkizze() {
      const img = new Image();
      img.onload = () => {
        const imgRatio = this.skizzeHeight / this.skizzeWidth;
        const imgHeight = imgRatio * this.cellWidth;

        this.canvas.width = this.cellWidth;
        this.canvas.height = imgHeight;

        this.ctx.drawImage(img, 0, 0, this.cellWidth, imgHeight);

        this.$nextTick(() => {
          if (this.input[this.cellKey] !== undefined) {
            const xRatio = this.skizzeWidth / this.canvas.width;
            const yRatio = this.skizzeHeight / this.canvas.height;

            // Draw circles on re-entering paint view
            this.input[this.cellKey].forEach((coordinates) => {
              let reTransformedX = coordinates.x / xRatio;
              let reTransformedY = coordinates.y / yRatio;
              this.drawCircle(Math.round(reTransformedX), Math.round(reTransformedY));
            });
          }
        });
      };
      if (this.skizzeUrl) {
        img.src = this.skizzeUrl;
      } else {
        const imgRatio = this.skizzeHeight / this.skizzeWidth;
        const imgHeight = imgRatio * this.cellWidth;

        this.canvas.width = this.cellWidth;
        this.canvas.height = imgHeight;

        this.ctx.fillStyle = '#000';
        this.ctx.font = '16px Roboto, sans-serif';

        const textString = this.$t('module.wagenbuch.fahrzeug.missing');
        const textMetrics = this.ctx.measureText(textString);

        const textWidth = textMetrics.width;
        const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent;

        this.ctx.fillText(textString, this.canvas.width / 2 - textWidth / 2, this.canvas.height / 2 - textHeight / 2);
      }
    },
    onImgClick(e, key) {
      if (this.skizzeUrl) {
        const el = document.getElementById(this.canvasId);
        const offset = el.getBoundingClientRect();

        const x = e.pageX - offset.left;
        const y = e.pageY - offset.top;

        const xRatio = this.skizzeWidth / offset.width;
        const yRatio = this.skizzeHeight / offset.height;

        let transformedX = Math.round(x * xRatio);
        let transformedY = Math.round(y * yRatio);

        this.drawCircle(x, y);

        if (!this.input[key]) {
          this.$set(this.input, key, [{ x: transformedX, y: transformedY }]);
        } else {
          this.input[key].push({ x: transformedX, y: transformedY });
        }
      }
    },
    drawCircle(x, y) {
      this.ctx.strokeStyle = 'red';
      this.ctx.beginPath();
      this.ctx.arc(x, y, this.getCircleSize(), 0, Math.PI * 2);
      this.ctx.stroke();
    },
    getCircleSize() {
      return Math.min(Math.round((15.0 / this.skizzeWidth) * this.canvas.width), 15);
    },
  },
  watch: {
    cellWidth() {
      this.showSkizze();
    },
    skizzeUrl: {
      handler(value) {
        this.setSkizzeDimensions();
      },
      immediate: true,
    },
    selectedFz(newValue, oldValue) {
      if (JSON.stringify(newValue) != JSON.stringify(oldValue)) {
        if (this.namespace === 'unfalldesk') {
          if (this.cell.cellName !== 'UDFremdFahrzeugPaintCell') {
            this.clearSkizze(this.cellKey);
          }
        }
      }
    },
  },
};
</script>

<style scoped>
canvas {
  width: 100%;
  border: 1px solid rgba(99, 99, 99, 0.2);
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
}

.hide {
  display: none;
}

.show {
  display: inline;
}
</style>
